import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Tag, Button, Select, Input, Checkbox, Tabs, Empty, Spin, message, Popconfirm, Tooltip, Card, Row, Col, Statistic, Progress, Dropdown } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, DownOutlined } from "@ant-design/icons";
import {
  fetchCategorizationStats,
  fetchGoldStandards,
  removeGoldStandard,
  fetchNegativeExamples,
  removeNegativeExample,
  fetchCategorizationPredictions,
  fetchNotificationLetterExemplars,
  removeNotificationLetterExemplar,
  addNotificationLetterExemplar,
  patchNotificationLetterExemplar,
} from "../../../service/matter";
import AddGoldStandardModal from "../../../components/Categorization/AddGoldStandardModal";
import EditGoldStandardModal from "../../../components/Categorization/EditGoldStandardModal";
import EditNegativeExampleModal from "../../../components/Categorization/EditNegativeExampleModal";
import GoldStandardLibraryGrid from "../../../components/Categorization/GoldStandardLibraryGrid";
import {
  AddNotificationLetterModal,
  EditNotificationLetterModal,
} from "../../../components/Categorization/NotificationLetterModal";
import { canCurateNotificationLetters } from "../../../components/Categorization/notificationLetterCuration";
import useAuth from "../../../hooks/useAuth";
import { SUBTYPE_NONE } from "../../../components/Categorization/SubtypeSelect";
import {
  fetchCategories,
  selectCategorizationCategories,
  selectCategorizationSubtypes,
  createGoldStandardFromDocument,
  patchGoldStandard,
  patchNegativeExample,
} from "../../../slice/CategorizationSlice";

const CATEGORY_COLORS = {
  "Agency Decisions": "#7b1fa2",
  "Appeals": "#d32f2f",
  "Applications": "#0C53BB",
  "Attorneys Fees": "#388e3c",
  "Codesheets": "#f9a825",
  "Correspondence": "#e64a19",
  "DBQs": "#0097a7",
  "DD-214 Military Admin": "#5d4037",
  "Exam Scheduling": "#6a1b9a",
  "Lay Statements": "#c2185b",
  "Miscellaneous": "#616161",
  "Post-Service Medical Records": "#0288d1",
  "Service Treatment Records": "#455a64",
};

const AICategorization = () => {
  const dispatch = useDispatch();
  const sliceCategories = useSelector(selectCategorizationCategories);
  const subtypes = useSelector(selectCategorizationSubtypes);
  // Notification-letter curation is Matthew-only (2026-08-12); other
  // admins see the library read-only. Server enforces the same gate.
  const { user } = useAuth();
  const canCurateNotifLetters = canCurateNotificationLetters(user);

  const [stats, setStats] = useState(null);
  const [goldData, setGoldData] = useState(null);
  const [negData, setNegData] = useState(null);
  const [notifData, setNotifData] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubtype, setSelectedSubtype] = useState(null);
  const [goldSearch, setGoldSearch] = useState("");
  const [negSearch, setNegSearch] = useState("");
  const [predFilter, setPredFilter] = useState({ category: null, correctedOnly: false });
  const [activeTab, setActiveTab] = useState("overview");

  // Modal state
  const [addGoldOpen, setAddGoldOpen] = useState(false);
  const [addGoldSaving, setAddGoldSaving] = useState(false);
  const [addGoldSuccess, setAddGoldSuccess] = useState(false);
  const [addGoldError, setAddGoldError] = useState("");

  const [editGoldRow, setEditGoldRow] = useState(null);
  const [editGoldSaving, setEditGoldSaving] = useState(false);
  const [editGoldError, setEditGoldError] = useState("");

  const [editNegRow, setEditNegRow] = useState(null);
  const [editNegSaving, setEditNegSaving] = useState(false);
  const [editNegError, setEditNegError] = useState("");

  const [addNotifOpen, setAddNotifOpen] = useState(false);
  const [addNotifSaving, setAddNotifSaving] = useState(false);
  const [addNotifSuccess, setAddNotifSuccess] = useState(false);
  const [addNotifError, setAddNotifError] = useState("");
  const [editNotifRow, setEditNotifRow] = useState(null);
  const [editNotifSaving, setEditNotifSaving] = useState(false);
  const [editNotifError, setEditNotifError] = useState("");
  const [notifSearch, setNotifSearch] = useState("");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, g, n, p, nl] = await Promise.all([
        fetchCategorizationStats(),
        fetchGoldStandards(),
        fetchNegativeExamples(),
        fetchCategorizationPredictions({ limit: 50 }),
        // Tolerate a backend that predates the exemplar library — the
        // tab renders its empty state instead of sinking the page.
        fetchNotificationLetterExemplars().catch(() => null),
      ]);
      setStats(s);
      setGoldData(g);
      setNegData(n);
      setPredictions(p);
      setNotifData(nl);
    } catch (err) {
      console.error("Failed to load categorization data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Pull the canonical category list for the add/edit modals. Fires once
  // on mount — `fetchCategories` is idempotent in the slice and the
  // results live in Redux so other surfaces (the right-click report
  // modal in matter rows) share the same cache.
  useEffect(() => {
    if (!sliceCategories || sliceCategories.length === 0) {
      dispatch(fetchCategories());
    }
  }, [dispatch, sliceCategories]);

  const modalCategories = useMemo(() => {
    if (sliceCategories && sliceCategories.length > 0) return sliceCategories;
    return stats?.available_categories || [];
  }, [sliceCategories, stats]);

  const handleAddGoldSubmit = async ({ documentId, category, subtype, notes }) => {
    setAddGoldSaving(true);
    setAddGoldError("");
    try {
      const result = await dispatch(
        createGoldStandardFromDocument({ documentId, category, subtype, notes })
      );
      if (createGoldStandardFromDocument.fulfilled.match(result)) {
        setAddGoldSuccess(true);
        message.success(`Added to gold standard library as '${category}'.`);
        setTimeout(() => {
          setAddGoldOpen(false);
          setAddGoldSuccess(false);
          loadAll();
        }, 600);
      } else {
        const payload = result.payload || {};
        setAddGoldError(
          payload.message || "Couldn't add to library — try again."
        );
      }
    } catch (err) {
      setAddGoldError("Couldn't add to library — try again.");
    } finally {
      setAddGoldSaving(false);
    }
  };

  const handleEditGoldSubmit = async ({ notes, subtype }) => {
    if (!editGoldRow?.id) return;
    setEditGoldSaving(true);
    setEditGoldError("");
    try {
      const data = { notes };
      // Only send the subtype when it actually changed, so editing notes
      // on a backend without subtypes doesn't post an unknown field.
      if ((subtype || "") !== (editGoldRow.subtype || "")) {
        data.subtype = subtype || "";
      }
      const result = await dispatch(
        patchGoldStandard({ id: editGoldRow.id, data })
      );
      if (patchGoldStandard.fulfilled.match(result)) {
        message.success(
          data.subtype !== undefined ? "Gold standard updated." : "Notes updated."
        );
        setEditGoldRow(null);
        loadAll();
      } else {
        const payload = result.payload || {};
        setEditGoldError(payload.message || "Couldn't save changes — try again.");
      }
    } catch (err) {
      setEditGoldError("Couldn't save changes — try again.");
    } finally {
      setEditGoldSaving(false);
    }
  };

  const handleEditNegSubmit = async (data) => {
    if (!editNegRow?.id) return;
    setEditNegSaving(true);
    setEditNegError("");
    try {
      const result = await dispatch(
        patchNegativeExample({ id: editNegRow.id, data })
      );
      if (patchNegativeExample.fulfilled.match(result)) {
        message.success("Report updated.");
        setEditNegRow(null);
        loadAll();
      } else {
        const payload = result.payload || {};
        setEditNegError(payload.message || "Couldn't save changes — try again.");
      }
    } catch (err) {
      setEditNegError("Couldn't save changes — try again.");
    } finally {
      setEditNegSaving(false);
    }
  };

  const handleRemoveGold = async (id) => {
    try {
      await removeGoldStandard(id);
      message.success("Gold standard removed");
      loadAll();
    } catch { message.error("Failed to remove"); }
  };

  const handleRemoveNeg = async (id) => {
    try {
      await removeNegativeExample(id);
      message.success("Negative example removed");
      loadAll();
    } catch { message.error("Failed to remove"); }
  };

  const handleRemoveNotif = async (id) => {
    try {
      await removeNotificationLetterExemplar(id);
      message.success("Notification letter example removed");
      loadAll();
    } catch { message.error("Failed to remove"); }
  };

  const handleAddNotifSubmit = async (payload) => {
    setAddNotifSaving(true);
    setAddNotifError("");
    try {
      await addNotificationLetterExemplar(payload);
      setAddNotifSuccess(true);
      message.success("Added to the notification letter library.");
      setTimeout(() => {
        setAddNotifOpen(false);
        setAddNotifSuccess(false);
        loadAll();
      }, 600);
    } catch (err) {
      setAddNotifError(
        err?.response?.data?.error || "Couldn't add to library — try again."
      );
    } finally {
      setAddNotifSaving(false);
    }
  };

  const handleEditNotifSubmit = async (data) => {
    if (!editNotifRow?.id) return;
    setEditNotifSaving(true);
    setEditNotifError("");
    try {
      await patchNotificationLetterExemplar(editNotifRow.id, data);
      message.success("Notification letter example updated.");
      setEditNotifRow(null);
      loadAll();
    } catch (err) {
      setEditNotifError(
        err?.response?.data?.error || "Couldn't save changes — try again."
      );
    } finally {
      setEditNotifSaving(false);
    }
  };

  const handlePredFilterChange = async () => {
    try {
      const data = await fetchCategorizationPredictions({
        category: predFilter.category,
        correctedOnly: predFilter.correctedOnly,
        limit: 100,
      });
      setPredictions(data);
    } catch { message.error("Failed to filter predictions"); }
  };

  useEffect(() => { if (stats) handlePredFilterChange(); }, [predFilter]);

  if (loading && !stats) {
    return <div style={{ textAlign: "center", padding: 60 }}><Spin size="large" /></div>;
  }

  const categories = stats?.available_categories || [];
  const goldCounts = stats?.gold_standard_counts || {};
  const negCounts = stats?.negative_example_counts || {};

  // Build accuracy table data
  const categoryTableData = (stats?.categories || []).map((c) => ({
    key: c.category,
    ...c,
    goldCount: goldCounts[c.category] || 0,
    negCount: negCounts[c.category] || 0,
  }));

  const goldStandards = goldData?.gold_standards || {};

  // Negative example flat list
  const negExamples = negData?.negative_examples || {};
  const negListRaw = selectedCategory
    ? (negExamples[selectedCategory] || []).map((i) => ({
        ...i,
        _category: selectedCategory,
      }))
    : Object.entries(negExamples).flatMap(([cat, items]) =>
        items.map((i) => ({ ...i, _category: cat }))
      );
  const negList = negSearch.trim()
    ? negListRaw.filter((row) => {
        const haystack = [
          row.filename,
          row.reason,
          row.notes,
          row.actual_category,
          row._category,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(negSearch.trim().toLowerCase());
      })
    : negListRaw;

  const predList = predictions?.predictions || [];
  const notifList = notifData?.exemplars || [];

  // Same curation surface as the Gold Standards, teaching a different
  // question — group the cards by the lesson each letter teaches. The
  // subtype tag slot carries what was attached in that scan.
  const NOTIF_PACKET_GROUP = "Calendar a deadline — decision attached";
  const NOTIF_STANDALONE_GROUP = "Do NOT calendar — nothing attached";
  const notifSectionLabel = (sections) => {
    const parts = [];
    if ((sections || []).includes("rating_decision")) parts.push("Rating Decision");
    if ((sections || []).includes("codesheet")) parts.push("Codesheet");
    return parts.length > 0 ? parts.join(" + ") : "Nothing attached";
  };
  const notifGrouped = { [NOTIF_PACKET_GROUP]: [], [NOTIF_STANDALONE_GROUP]: [] };
  notifList.forEach((ex) => {
    const group =
      (ex.enclosed_sections || []).length > 0
        ? NOTIF_PACKET_GROUP
        : NOTIF_STANDALONE_GROUP;
    notifGrouped[group].push({
      ...ex,
      subtype: "enclosures",
      subtype_label: notifSectionLabel(ex.enclosed_sections),
    });
  });

  const tabItems = [
    {
      key: "overview",
      label: "Accuracy Overview",
      children: (
        <div style={{ overflow: "auto" }}>
          {/* Summary cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Total Predictions"
                  value={stats?.total_predictions || 0}
                  valueStyle={{ color: "#0C53BB" }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Corrections"
                  value={stats?.total_corrections || 0}
                  valueStyle={{ color: stats?.total_corrections > 0 ? "#d32f2f" : "#388e3c" }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Accuracy"
                  value={stats?.overall_accuracy_pct || 100}
                  suffix="%"
                  valueStyle={{ color: (stats?.overall_accuracy_pct || 100) >= 90 ? "#388e3c" : "#d32f2f" }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Gold Standards"
                  value={Object.values(goldCounts).reduce((a, b) => a + b, 0)}
                  valueStyle={{ color: "#f9a825" }}
                />
              </Card>
            </Col>
          </Row>

          {/* 7d / 30d trends */}
          {stats?.trends && (
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={12}>
                <Card size="small" title="Last 7 Days">
                  <div style={{ display: "flex", gap: 24 }}>
                    <Statistic title="Predictions" value={stats.trends["7d"]?.predictions || 0} />
                    <Statistic title="Corrections" value={stats.trends["7d"]?.corrections || 0} />
                  </div>
                </Card>
              </Col>
              <Col xs={12}>
                <Card size="small" title="Last 30 Days">
                  <div style={{ display: "flex", gap: 24 }}>
                    <Statistic title="Predictions" value={stats.trends["30d"]?.predictions || 0} />
                    <Statistic title="Corrections" value={stats.trends["30d"]?.corrections || 0} />
                  </div>
                </Card>
              </Col>
            </Row>
          )}

          {/* Per-category table */}
          <Table
            dataSource={categoryTableData}
            pagination={false}
            size="small"
            columns={[
              {
                title: "Category",
                dataIndex: "category",
                render: (text) => (
                  <Tag color={CATEGORY_COLORS[text] || "#666"}>{text}</Tag>
                ),
              },
              { title: "Predictions", dataIndex: "total", sorter: (a, b) => a.total - b.total },
              { title: "Corrections", dataIndex: "corrections", sorter: (a, b) => a.corrections - b.corrections },
              {
                title: "Accuracy",
                dataIndex: "accuracy_pct",
                sorter: (a, b) => a.accuracy_pct - b.accuracy_pct,
                render: (v) => (
                  <Progress
                    percent={v}
                    size="small"
                    strokeColor={v >= 90 ? "#52c41a" : v >= 75 ? "#faad14" : "#ff4d4f"}
                    style={{ minWidth: 100 }}
                  />
                ),
              },
              {
                title: "Avg Confidence",
                dataIndex: "avg_confidence",
                render: (v) => `${v}%`,
              },
              {
                title: "Gold Stds",
                dataIndex: "goldCount",
                render: (v) => (
                  <span style={{ color: v >= 5 ? "#388e3c" : v > 0 ? "#f9a825" : "#999" }}>
                    {v}
                  </span>
                ),
              },
              {
                title: "Neg Examples",
                dataIndex: "negCount",
              },
            ]}
          />
        </div>
      ),
    },
    {
      key: "gold",
      label: `Gold Standards (${Object.values(goldCounts).reduce((a, b) => a + b, 0)})`,
      children: (
        <div>
          <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
            <Input.Search
              allowClear
              placeholder="Search filename, summary, or notes"
              style={{ width: 320 }}
              value={goldSearch}
              onChange={(e) => setGoldSearch(e.target.value)}
              data-testid="gold-search"
            />
            <Select
              allowClear
              placeholder="Filter by category"
              style={{ width: 250 }}
              value={selectedCategory}
              onChange={setSelectedCategory}
              options={categories.map((c) => ({ value: c, label: c }))}
            />
            {subtypes.length > 0 && (
              <Select
                allowClear
                placeholder="Filter by subtype"
                style={{ width: 230 }}
                value={selectedSubtype}
                onChange={setSelectedSubtype}
                data-testid="gold-subtype-filter"
                options={[
                  // Finding the untagged rows is the whole curation
                  // workflow: a library of category-only examples needs
                  // to be worked through, and this is the worklist.
                  { value: SUBTYPE_NONE, label: "Not yet tagged" },
                  ...subtypes.map((s) => ({
                    value: s.value,
                    label: s.label || s.value,
                  })),
                ]}
              />
            )}
            <Button icon={<ReloadOutlined />} onClick={loadAll}>Refresh</Button>
          </div>

          <GoldStandardLibraryGrid
            goldStandards={goldStandards}
            selectedCategory={selectedCategory}
            selectedSubtype={selectedSubtype}
            searchText={goldSearch}
            loading={loading && !goldData}
            onEdit={setEditGoldRow}
            onRemove={handleRemoveGold}
          />
        </div>
      ),
    },
    {
      key: "negative",
      label: `Negative Examples (${Object.values(negCounts).reduce((a, b) => a + b, 0)})`,
      children: (
        <div>
          <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
            <Input.Search
              allowClear
              placeholder="Search filename, reason, or notes"
              style={{ width: 320 }}
              value={negSearch}
              onChange={(e) => setNegSearch(e.target.value)}
              data-testid="negative-search"
            />
            <Select
              allowClear
              placeholder="Filter by category"
              style={{ width: 250 }}
              value={selectedCategory}
              onChange={setSelectedCategory}
              options={categories.map((c) => ({ value: c, label: c }))}
            />
            <Button icon={<ReloadOutlined />} onClick={loadAll}>Refresh</Button>
          </div>

          {negList.length === 0 ? (
            <Empty
              description={
                negListRaw.length === 0
                  ? "No negative examples yet. These are created when correcting a miscategorized document."
                  : "No negative examples match your search."
              }
            />
          ) : (
            <Table
              dataSource={negList.map((n, i) => ({ key: n.id || i, ...n }))}
              pagination={{ pageSize: 20 }}
              size="small"
              columns={[
                {
                  title: "NOT This Category",
                  dataIndex: selectedCategory ? "negative_for_category" : "_category",
                  render: (text) => (
                    <Tooltip title="This document is NOT this category">
                      <Tag color="red">{text}</Tag>
                    </Tooltip>
                  ),
                },
                { title: "Document", dataIndex: "filename", ellipsis: true },
                {
                  title: "Actual Category",
                  dataIndex: "actual_category",
                  render: (text) => <Tag color={CATEGORY_COLORS[text] || "#666"}>{text}</Tag>,
                },
                {
                  title: "Reason",
                  dataIndex: "reason",
                  ellipsis: true,
                  render: (v) => <span style={{ color: "#666", fontSize: 12 }}>{v || "—"}</span>,
                },
                {
                  title: "",
                  width: 80,
                  render: (_, record) => (
                    <span>
                      <Tooltip title="Edit report">
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          size="small"
                          onClick={() => setEditNegRow(record)}
                          data-testid={`negative-edit-${record.id}`}
                        />
                      </Tooltip>
                      <Popconfirm
                        title="Remove this negative example?"
                        onConfirm={() => handleRemoveNeg(record.id)}
                      >
                        <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                      </Popconfirm>
                    </span>
                  ),
                },
              ]}
            />
          )}
        </div>
      ),
    },
    {
      key: "notification-letters",
      label: `Notification Letters (${notifList.length})`,
      children: (
        <div>
          <div style={{ marginBottom: 16, color: "#5a6c87", fontSize: 13, maxWidth: 720 }}>
            Verified examples of VA decision-notification letters and what
            was actually attached behind each one. The system compares
            incoming mail against these to recognize letters that need a
            deadline calendared — and the &ldquo;nothing attached&rdquo;
            counter-examples teach it what to leave alone. This library
            does not affect folder categorization.
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
            <Input.Search
              allowClear
              placeholder="Search filename, summary, or notes"
              style={{ width: 320 }}
              value={notifSearch}
              onChange={(e) => setNotifSearch(e.target.value)}
              data-testid="notification-letter-search"
            />
            <Button icon={<ReloadOutlined />} onClick={loadAll}>Refresh</Button>
          </div>
          <GoldStandardLibraryGrid
            goldStandards={notifGrouped}
            searchText={notifSearch}
            loading={loading && !notifData}
            onEdit={setEditNotifRow}
            onRemove={handleRemoveNotif}
            groupColors={{
              [NOTIF_PACKET_GROUP]: "#388e3c",
              [NOTIF_STANDALONE_GROUP]: "#8c8c8c",
            }}
            removeConfirmTitle="Remove this notification letter example?"
            readOnly={!canCurateNotifLetters}
            emptyState={
              <Empty description="No notification letter examples yet. Add one with the 'Add example' button, or they are seeded from verified mail packets." />
            }
          />
        </div>
      ),
    },
    {
      key: "predictions",
      label: "Prediction Log",
      children: (
        <div>
          <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center" }}>
            <Select
              allowClear
              placeholder="Filter by category"
              style={{ width: 220 }}
              value={predFilter.category}
              onChange={(v) => setPredFilter((p) => ({ ...p, category: v }))}
              options={categories.map((c) => ({ value: c, label: c }))}
            />
            <Checkbox
              checked={predFilter.correctedOnly}
              onChange={(e) => setPredFilter((p) => ({ ...p, correctedOnly: e.target.checked }))}
            >
              Corrections only
            </Checkbox>
            <Button icon={<ReloadOutlined />} onClick={loadAll}>Refresh</Button>
          </div>

          <Table
            dataSource={predList.map((p) => ({ key: p.id, ...p }))}
            pagination={{ pageSize: 25 }}
            size="small"
            columns={[
              {
                title: "Document",
                dataIndex: "filename",
                ellipsis: true,
                width: 220,
              },
              {
                title: "Predicted",
                dataIndex: "predicted_category",
                render: (text) => <Tag color={CATEGORY_COLORS[text] || "#666"}>{text}</Tag>,
              },
              {
                title: "Final",
                dataIndex: "final_category",
                render: (text, record) =>
                  record.was_corrected ? (
                    <Tag color={CATEGORY_COLORS[text] || "#666"} icon={<EditOutlined />}>{text}</Tag>
                  ) : (
                    <Tag color={CATEGORY_COLORS[text] || "#666"}>{text}</Tag>
                  ),
              },
              {
                title: "Confidence",
                dataIndex: "confidence_score",
                width: 90,
                render: (v) => (
                  <span style={{ color: v >= 80 ? "#388e3c" : v >= 60 ? "#f9a825" : "#d32f2f" }}>
                    {v}%
                  </span>
                ),
              },
              {
                title: "Status",
                dataIndex: "was_corrected",
                width: 90,
                render: (v) => v ? (
                  <Tag icon={<CloseCircleOutlined />} color="error">Corrected</Tag>
                ) : (
                  <Tag icon={<CheckCircleOutlined />} color="success">OK</Tag>
                ),
              },
              {
                title: "Reason",
                dataIndex: "correction_reason",
                ellipsis: true,
                render: (v) => <span style={{ color: "#666", fontSize: 12 }}>{v || "—"}</span>,
              },
              {
                title: "Model",
                dataIndex: "model_name",
                width: 130,
                ellipsis: true,
                render: (v) => <span style={{ fontSize: 11, color: "#888" }}>{v || "—"}</span>,
              },
              {
                title: "Date",
                dataIndex: "created_at",
                width: 130,
                render: (v) => {
                  if (!v) return "—";
                  const d = new Date(v);
                  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                },
              },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <div style={{ height: "100%", overflow: "auto", paddingRight: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, fontFamily: "Manrope", color: "#1b2028" }}>
            AI Categorization
          </div>
          <div style={{ fontSize: 13, color: "#5a6c87", marginTop: 2 }}>
            Manage training data and monitor categorization accuracy
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Dropdown
            menu={{
              items: [
                {
                  key: "gold",
                  label: "Add gold standard",
                  onClick: () => {
                    setAddGoldError("");
                    setAddGoldSuccess(false);
                    setAddGoldOpen(true);
                  },
                },
                ...(canCurateNotifLetters
                  ? [
                      {
                        key: "notification-letter",
                        label: "Add notification letter",
                        onClick: () => {
                          setAddNotifError("");
                          setAddNotifSuccess(false);
                          setAddNotifOpen(true);
                        },
                      },
                    ]
                  : []),
              ],
            }}
            trigger={["click"]}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              data-testid="settings-add-example-btn"
            >
              Add example <DownOutlined />
            </Button>
          </Dropdown>
          <Button icon={<ReloadOutlined />} onClick={loadAll} loading={loading}>
            Refresh
          </Button>
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="small"
      />

      <AddGoldStandardModal
        open={addGoldOpen}
        categories={modalCategories}
        subtypes={subtypes}
        saving={addGoldSaving}
        success={addGoldSuccess}
        error={addGoldError}
        onCancel={() => {
          if (addGoldSaving) return;
          setAddGoldOpen(false);
          setAddGoldError("");
        }}
        onSubmit={handleAddGoldSubmit}
      />

      <EditGoldStandardModal
        open={Boolean(editGoldRow)}
        row={editGoldRow}
        subtypes={subtypes}
        saving={editGoldSaving}
        error={editGoldError}
        onCancel={() => {
          if (editGoldSaving) return;
          setEditGoldRow(null);
          setEditGoldError("");
        }}
        onSubmit={handleEditGoldSubmit}
      />

      <EditNegativeExampleModal
        open={Boolean(editNegRow)}
        row={editNegRow}
        categories={modalCategories}
        saving={editNegSaving}
        error={editNegError}
        onCancel={() => {
          if (editNegSaving) return;
          setEditNegRow(null);
          setEditNegError("");
        }}
        onSubmit={handleEditNegSubmit}
      />

      <AddNotificationLetterModal
        open={addNotifOpen}
        saving={addNotifSaving}
        success={addNotifSuccess}
        error={addNotifError}
        onCancel={() => {
          if (addNotifSaving) return;
          setAddNotifOpen(false);
          setAddNotifError("");
        }}
        onSubmit={handleAddNotifSubmit}
      />

      <EditNotificationLetterModal
        open={Boolean(editNotifRow)}
        row={editNotifRow}
        saving={editNotifSaving}
        error={editNotifError}
        onCancel={() => {
          if (editNotifSaving) return;
          setEditNotifRow(null);
          setEditNotifError("");
        }}
        onSubmit={handleEditNotifSubmit}
      />
    </div>
  );
};

export default AICategorization;
